import './HvacFormSection.scss';
import { FieldPath } from '@form-ts/core';
import { useFormField } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Accordion } from 'src/modules/common/components/Accordion';
import { FormError } from 'src/modules/form/types/FormError';
import { HvacFormData, HvacFormSectionData } from 'src/modules/hvac/types/HvacFormData';
import { HvacFormSectionContent } from 'src/modules/hvac/views/HvacFormSectionContent';
import { HvacFormSectionHeader } from 'src/modules/hvac/views/HvacFormSectionHeader';

type Props = {
  readonly path: FieldPath<HvacFormData, FormError, HvacFormSectionData>;
  readonly opened: boolean;
  readonly readOnly: boolean;
  readonly onToggle: (opened: boolean) => void;
};

export const HvacFormSection = ({
  path,
  opened,
  readOnly,
  onToggle,
}: Props): React.ReactElement => {
  const { value: section } = useFormField(path);

  const container = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (opened) {
      container.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [opened]);

  const handleClick = useCallback(() => {
    onToggle(!opened);
  }, [opened, onToggle]);

  const handleToggle = useCallback(() => {
    const current = path.value.get(path.form.currentState);
    const enabled = !current.enabled;

    onToggle(enabled);

    path.form.change(pipe(
      path.form.currentState,
      path.at('enabled').value.set(enabled),
    ));
  }, [path, onToggle]);

  const header = useMemo(() => (
    <HvacFormSectionHeader
      id={path.path}
      actual={section.answer.actual.option?.class ?? null}
      future={section.answer.future.option?.class ?? null}
      checked={section.enabled}
      disabled={!section.config.deactivation}
      readOnly={readOnly}
      hasImpact={section.config.has_impact}
      onToggle={handleToggle}
      onClick={handleClick}
    >
      {section.config.has_impact
        ? <>{section.config.number}&nbsp;<FormattedMessage id={section.config.name}/></>
        : <FormattedMessage id={section.config.name}/>}
    </HvacFormSectionHeader>
  ), [
    handleToggle,
    handleClick,
    section,
    readOnly,
    path.path,
  ]);

  return (
    <div
      ref={container}
      className="bp-hvac-form-section"
    >
      <Accordion
        open={opened}
        disabled={!section.enabled}
        header={header}
      >
        <HvacFormSectionContent
          path={path.at('answer')}
          disabled={!section.enabled}
          config={section.config}
          readOnly={readOnly}
          hasImpact={section.config.has_impact}
        />
      </Accordion>
    </div>
  );
};
